import { useCallback, useEffect, useState } from 'react';
import { fetchCollectors, type CollectorRow, type CollectorRun } from '../../api/work';
import './pulse-panels.css';

/**
 * The bottom of the stack — what the mirror is actually being fed.
 *
 * Last tab in the conclusions-first order, and the first place to look
 * when a number upstream is stale: every metric, observation and insight
 * is computed over the mirror, and the mirror is only as current as these.
 *
 * **`registered: false` is a finding, not an absence.** The route returns
 * the union of collectors registered this boot and collectors that have
 * ever run, so a row with runs and no registration is one that *used to*
 * collect and no longer does — a retired or unwired collector, which looks
 * identical to a healthy one if you only read `run_count`. It is marked.
 *
 * **A collector with runs and no `last_success_at_ms` has never
 * succeeded.** Distinct from one that has never run, and the more
 * dangerous of the two: it is doing work and storing nothing.
 */

function relative(ms: number | null): string {
  if (ms === null) return 'never';
  const min = Math.floor((Date.now() - ms) / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

function ms(v: number): string {
  if (v < 1000) return `${Math.round(v)}ms`;
  if (v < 60_000) return `${(v / 1000).toFixed(1)}s`;
  const m = Math.floor(v / 60_000);
  const s = Math.round((v % 60_000) / 1000);
  return s === 0 ? `${m}m` : `${m}m${s}s`;
}

/** Errors arrive as a JSON string, or null. Never trusted to parse. */
function errorLines(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.map((e) => String(e));
    return [JSON.stringify(parsed)];
  } catch {
    return [raw];
  }
}

function RunRow({ run }: { run: CollectorRun }) {
  const errs = errorLines(run.errors);
  return (
    <tr>
      <td className="pp__dim">{relative(run.started_at_ms)}</td>
      <td className="pp__num">{ms(run.duration_ms)}</td>
      <td className="pp__num">{run.captured}</td>
      <td className="pp__num">{run.inserted}</td>
      <td className="pp__num">{run.updated}</td>
      <td className="pp__num">{run.skipped}</td>
      <td>
        {errs.length === 0 ? (
          <span className="pp__dim">—</span>
        ) : (
          <span className="pp__err" title={errs.join('\n')}>
            {errs.length} error{errs.length === 1 ? '' : 's'}
          </span>
        )}
      </td>
    </tr>
  );
}

export function CollectorsPanel() {
  const [rows, setRows] = useState<CollectorRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(() => {
    fetchCollectors()
      .then((r) => {
        setRows(r.collectors);
        setError(null);
      })
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : String(e)),
      );
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  if (error) return <p className="pp__error">{error}</p>;
  if (rows === null) return <p className="pp__dim">Loading…</p>;
  if (rows.length === 0) {
    return (
      <p className="pp__dim">
        No collectors. <code>work.collect</code> registers them at startup,
        so an empty list means the extension is not running — every number
        in the other tabs is then as old as the last time it was.
      </p>
    );
  }

  return (
    <div className="pp">
      <div className="pp__bar">
        <span className="pp__dim">
          {rows.length} collector{rows.length === 1 ? '' : 's'} · refreshes
          every 30s
        </span>
        <button type="button" className="pp__btn" onClick={load}>
          Refresh
        </button>
      </div>

      <ul className="pp__cards">
        {rows.map((c) => {
          const neverSucceeded = c.run_count > 0 && c.last_success_at_ms === null;
          const isOpen = open === c.name;
          return (
            <li
              key={c.name}
              className={`pp__card${!c.registered ? ' is-warn' : ''}${
                neverSucceeded ? ' is-bad' : ''
              }`}
            >
              <div className="pp__card-top">
                <span className="pp__strong">{c.name}</span>
                {!c.registered && (
                  <span
                    className="pp__badge pp__badge--warn"
                    title="Has run history but was not registered this boot — a retired or unwired collector, which reads as healthy if you only look at the run count"
                  >
                    not registered
                  </span>
                )}
                {neverSucceeded && (
                  <span
                    className="pp__badge pp__badge--bad"
                    title="It has run and never succeeded — doing work and storing nothing"
                  >
                    never succeeded
                  </span>
                )}
              </div>
              <div className="pp__card-row pp__dim">
                <span>{c.run_count} runs</span>
                <span>·</span>
                <span className={c.error_count > 0 ? 'pp__err' : undefined}>
                  {c.error_count} errors
                </span>
                <span>·</span>
                <span>last success {relative(c.last_success_at_ms)}</span>
              </div>
              {c.last_run && (
                <div className="pp__card-row pp__dim">
                  last run {relative(c.last_run.started_at_ms)} ·{' '}
                  {ms(c.last_run.duration_ms)} · {c.last_run.captured} captured,{' '}
                  {c.last_run.inserted} new, {c.last_run.updated} updated,{' '}
                  {c.last_run.skipped} skipped
                </div>
              )}
              {c.recent_runs.length > 0 && (
                <button
                  type="button"
                  className="pp__btn pp__btn--link"
                  onClick={() => setOpen(isOpen ? null : c.name)}
                >
                  {isOpen ? 'hide' : `${c.recent_runs.length} recent runs`}
                </button>
              )}
              {isOpen && (
                <div className="pp__scroll">
                  <table className="pp__table">
                    <thead>
                      <tr>
                        <th>started</th>
                        <th className="pp__num">took</th>
                        <th className="pp__num">captured</th>
                        <th className="pp__num">new</th>
                        <th className="pp__num">updated</th>
                        <th className="pp__num">skipped</th>
                        <th>errors</th>
                      </tr>
                    </thead>
                    <tbody>
                      {c.recent_runs.map((r, i) => (
                        <RunRow key={`${r.started_at_ms}-${i}`} run={r} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
